// src/pages/MobileAppHome.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// 로그인 여부 확인
import { useAuth } from "../context/AuthProvider";

// 카카오톡 채널 버튼
import KakaoChatButton from "../components/KakaoChatButton";
import HomeNoticeModal from "../components/HomeNoticeModal";

// ✅ QR 플로팅 버튼

// 카드사 로고
import logoBC from "../assets/card-logos/bc.png";
import logoCiti from "../assets/card-logos/citi.png";
import logoHana from "../assets/card-logos/hana.png";
import logoHyundai from "../assets/card-logos/hyundai.png";
import logoJeju from "../assets/card-logos/jeju.png";
import logoJeonbuk from "../assets/card-logos/jeonbuk.png";
import logoKakaoBank from "../assets/card-logos/kakaobank.png";
import logoKB from "../assets/card-logos/kb.png";
import logoKBank from "../assets/card-logos/kbank.png";
import logoKwangju from "../assets/card-logos/kwangju.png";
import logoLotte from "../assets/card-logos/lotte.png";
import logoMG from "../assets/card-logos/mg.png";
import logoNonghyup from "../assets/card-logos/nonghyup.png";
import logoPost from "../assets/card-logos/post.png";
import logoSamsung from "../assets/card-logos/samsung.png";
import logoSave from "../assets/card-logos/save.png";
import logoSinhan from "../assets/card-logos/sinhan.png";
import logoSinhyup from "../assets/card-logos/sinhyup.png";
import logoSoohyup from "../assets/card-logos/soohyup.png";
import logoWoori from "../assets/card-logos/woori.png";

const API_BASE = (import.meta.env.VITE_API_BASE || "").replace(/\/$/, "");

// ✅ 배너 크기(높이) 조절 포인트
const BANNER_ASPECT = "1.75/1"; // 추천: 1.75 ~ 1.9

// ✅ 어플 다운로드 라우트(프로젝트에 맞게 유지)
const APP_DOWNLOAD_URL = "/download";

// 이용방법 4단계
const FLOW_ITEMS = [
  { step: 1, title: "증빙자료 등록", description: "계약/거래 관련 서류를 업로드합니다.", icon: "📄" },
  { step: 2, title: "자료 검토", description: "관리자가 서류를 검토합니다.", icon: "🔍" },
  { step: 3, title: "결제 등록", description: "승인된 건에 대해 카드 결제가 준비됩니다.", icon: "💳" },
  { step: 4, title: "10초 송금", description: "카드사 승인 즉시 빠르게 정산됩니다.", icon: "⚡" },
];

const INTRO_POINTS = [
  <>
    <span className="text-orange-500 font-semibold">주류 거래 대금</span>을{" "}
    <span className="text-orange-500 font-semibold">카드로 결제</span>하는 서비스
  </>,
  <>
    <span className="text-orange-500 font-semibold">현금 자금 흐름</span>을{" "}
    <span className="text-orange-500 font-semibold">안정적으로 관리</span> 가능
  </>,
  <>
    <span className="text-orange-500 font-semibold">단순 송금</span>이 아닌{" "}
    <span className="text-orange-500 font-semibold">실제 계약 기반</span> 서비스
  </>,
  <>
    수수료는 <span className="text-orange-500 font-semibold">업계 최저수수료 3.7%</span>이며,{" "}
    <span className="text-orange-500 font-semibold">부가세는 별도</span>로 추가됩니다.
  </>,
];

const TESTIMONIALS = [
  {
    quote: "외상 정산이 한 번에 정리돼서 월말 부담이 줄었어요.",
    name: "40대 도매상",
    tag: "외상 정산",
  },
  {
    quote: "증빙 관리가 쉬워지고 결제 흐름이 훨씬 투명해졌습니다.",
    name: "30대 매장 운영자",
    tag: "결제 관리",
  },
  {
    quote: "승인 후 바로 정산돼서 재고 회전이 빨라집니다.",
    name: "50대 유통업 종사자",
    tag: "빠른 정산",
  },
];

const FAQ_ITEMS = [
  {
    q: "서비스 이용 수수료는 얼마인가요?",
    a: "서비스 이용료는 업계 최저수수료 3.7%**이며, 부가세는 별도로 추가됩니다.",
  },
  {
    q: "어떤 서류가 필요한가요?",
    a: "거래품목별로 결제등록할 때 작성되어 있습니다. 제출하신 서류가 미비한 경우, 추가 증빙 자료를 요청드릴 수 있습니다.",
  },
  {
    q: "계약 승인은 얼마나 걸리나요?",
    a: "업로드하신 계약서 검토 후 승인 여부를 안내드립니다. 일반적으로 약 30분~1시간 내외이며, 서류 확인이 필요한 경우 시간이 더 걸릴 수 있습니다.",
  },
  {
    q: "카드 할부나 무이자 혜택도 이용할 수 있나요?",
    a: "네, 가능합니다. 무이자 할부 기간은 ‘무이자 할부 안내’에서 확인하실 수 있습니다.",
  },
];

type InstallmentRow = {
  id?: number;
  key: string;
  name: string;
  term: string;
  enabled?: boolean;
  sort_order?: number;

  partial_text?: string | null;
  partial_note?: string | null;
  partialText?: string | null;
  partialNote?: string | null;
};

type HomeBanner = {
  id: number;
  image_path: string;
  memo?: string | null;
  link_url?: string | null;
  enabled?: boolean;
  sort_order?: number;
};

const CARD_LOGOS: Record<string, string> = {
  bc: logoBC,
  kb: logoKB,
  sinhan: logoSinhan,
  samsung: logoSamsung,
  lotte: logoLotte,
  woori: logoWoori,
  nonghyup: logoNonghyup,
  jeonbuk: logoJeonbuk,
  kwangju: logoKwangju,
  hyundai: logoHyundai,
  citi: logoCiti,
  hana: logoHana,
  jeju: logoJeju,
  kakaobank: logoKakaoBank,
  mg: logoMG,
  post: logoPost,
  save: logoSave,
  sinhyup: logoSinhyup,
  soohyup: logoSoohyup,
};

type InstallmentCard = InstallmentRow & { logo?: string };

function joinApiUrl(p: string) {
  const path = String(p || "").trim();
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  if (!API_BASE) return path.startsWith("/") ? path : `/${path}`;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE}${normalized}`;
}

function normalizeTermDisplay(term: string): string {
  const t = String(term || "").trim();
  if (!t) return "";
  if (t.includes("불가")) return t;
  if (/\d/.test(t) && !/개월/.test(t)) return `${t}개월`;
  return t;
}

function normalizePartialText(row: any): string {
  const v = row?.partial_text ?? row?.partialText;
  return (v ? String(v) : "").trim();
}

function normalizePartialNote(row: any): string {
  const v = row?.partial_note ?? row?.partialNote;
  return (v ? String(v) : "").trim();
}

function BannerCarousel({
  items,
  onClick,
}: {
  items: HomeBanner[];
  onClick: (linkUrl?: string | null) => void;
}) {
  const [idx, setIdx] = useState(0);
  const timerRef = useRef<number | null>(null);
  const count = items.length;

  const startXRef = useRef<number | null>(null);
  const draggingRef = useRef(false);
  const movedRef = useRef(false);

  const stop = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const start = () => {
    stop();
    if (count <= 1) return;
    timerRef.current = window.setInterval(() => {
      setIdx((p) => (p + 1) % count);
    }, 3500);
  };

  useEffect(() => {
    start();
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  if (!count) return null;

  const go = (next: number) => {
    const n = ((next % count) + count) % count;
    setIdx(n);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    if (count <= 1) return;
    stop();
    draggingRef.current = true;
    movedRef.current = false;
    startXRef.current = e.touches[0]?.clientX ?? null;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!draggingRef.current) return;
    const x = e.touches[0]?.clientX ?? null;
    if (x == null || startXRef.current == null) return;
    if (Math.abs(x - startXRef.current) > 6) movedRef.current = true;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;

    const endX = e.changedTouches[0]?.clientX ?? null;
    const startX = startXRef.current;
    startXRef.current = null;

    if (endX == null || startX == null) {
      start();
      return;
    }

    const dx = endX - startX;
    const threshold = 45;

    if (Math.abs(dx) >= threshold) {
      if (dx < 0) go(idx + 1);
      else go(idx - 1);
    }

    start();
  };

  return (
    <div
      className="relative touch-pan-y break-keep"
      onMouseEnter={stop}
      onMouseLeave={start}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${idx * 100}%)` }}
        >
          {items.map((b) => {
            const src = joinApiUrl(b.image_path);
            const clickable = !!String(b.link_url || "").trim();

            const card = (
              <div className="relative w-full shrink-0">
                <div className="relative w-full bg-white" style={{ aspectRatio: BANNER_ASPECT }}>
                  <img
                    src={src}
                    alt=""
                    className="absolute inset-0 h-full w-full select-none object-cover blur-[10px] scale-[1.08] opacity-60"
                    aria-hidden="true"
                    draggable={false}
                  />
                  <img
                    src={src}
                    alt={b.memo ? String(b.memo) : `banner-${b.id}`}
                    className="relative h-full w-full select-none object-contain"
                    loading="lazy"
                    draggable={false}
                  />
                </div>
              </div>
            );

            const handleClick = () => {
              if (movedRef.current) return;
              onClick(b.link_url);
            };

            return clickable ? (
              <button
                key={b.id}
                type="button"
                onClick={handleClick}
                className="block w-full shrink-0 text-left transition active:scale-[0.995]"
              >
                {card}
              </button>
            ) : (
              <div key={b.id} className="w-full shrink-0">
                {card}
              </div>
            );
          })}
        </div>
      </div>

      {count > 1 && (
        <div className="mt-2 flex items-center justify-center gap-2">
          {items.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => go(i)}
              aria-label={`dot-${i + 1}`}
              className={[
                "h-2 w-2 rounded-full transition",
                i === idx ? "bg-slate-900" : "bg-slate-300",
              ].join(" ")}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function MobileAppHome() {
  const nav = useNavigate();
  const { user } = useAuth();

  const [installments, setInstallments] = useState<InstallmentCard[]>([]);
  const [banners, setBanners] = useState<HomeBanner[]>([]);
  const [partialOpen, setPartialOpen] = useState(false);
  const [testimonialIndex, setTestimonialIndex] = useState(0);

  useEffect(() => {
    const loadBanners = async () => {
      try {
        const url = API_BASE ? `${API_BASE}/api/banners` : "/api/banners";
        const res = await fetch(url);
        if (!res.ok) throw new Error("failed");
        const data = await res.json();
        const items = Array.isArray(data?.items) ? data.items : [];
        setBanners(items);
      } catch (e) {
        console.error("load banners error", e);
        setBanners([]);
      }
    };
    loadBanners();
  }, []);

  const onBannerClick = (linkUrl?: string | null) => {
    const u = String(linkUrl || "").trim();
    if (!u) return;

    if (/^https?:\/\//i.test(u)) {
      window.open(u, "_blank", "noreferrer");
      return;
    }
    nav(u.startsWith("/") ? u : `/${u}`);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const url = API_BASE ? `${API_BASE}/api/installments` : "/api/installments";
        const res = await fetch(url);
        if (!res.ok) throw new Error("failed");
        const data = await res.json();

        const raw =
          data?.items ||
          data?.installments ||
          data?.list ||
          data?.data ||
          data?.cards ||
          [];

        if (!Array.isArray(raw)) {
          setInstallments([]);
          return;
        }

        const mapped: InstallmentCard[] = raw
          .map((row: any) => {
            const key = String(row.key || "").trim();
            const name = String(row.name || "").trim();
            const term = String(row.term || "").trim();
            if (!key || !name) return null;

            return {
              id: row.id,
              key,
              name,
              term,
              enabled: row.enabled,
              sort_order: row.sort_order,
              partial_text: normalizePartialText(row) || null,
              partial_note: normalizePartialNote(row) || null,
              logo: CARD_LOGOS[key],
            } as InstallmentCard;
          })
          .filter((c): c is InstallmentCard => !!c && !!c.logo);

        setInstallments(mapped);
      } catch (e) {
        console.error("load installments error", e);
        setInstallments([]);
      }
    };

    load();
  }, []);

  const installmentDisplay = useMemo(() => {
    return installments.map((c) => ({ ...c, term: normalizeTermDisplay(c.term) }));
  }, [installments]);

  const partialCards = useMemo(() => {
    return installments
      .map((c) => ({
        ...c,
        partial_text: (c.partial_text || "").trim() || null,
        partial_note: (c.partial_note || "").trim() || null,
      }))
      .filter((c) => !!c.partial_text);
  }, [installments]);

  const goStart = () => {
    // ✅ 지금 시작하기: 로그인 여부 따라 이동(원하는 흐름 유지)
    if (user) return nav("/registration?type=liquor");
    return nav("/login");
  };

  const goDownload = () => {
    // 필요하면 외부 스토어 링크로 변경 가능
    nav(APP_DOWNLOAD_URL);
  };

  return (
    <div className="min-h-screen bg-brand-50 overflow-x-hidden break-keep">
      <div className="mx-auto max-w-[480px] px-3 pt-3 pb-24 space-y-4">
        {/* 상단 안내 카드 (CTA 버튼 단일/길게) */}
        <div className="-mx-1">
          <div className="rounded-[30px] bg-gradient-to-r from-brand-600 to-brand-500 px-4 py-5 text-white shadow-sm">
            <div className="text-[16px] font-extrabold leading-tight tracking-tight break-keep">
              <div>주류 대금 납부,</div>
              <div>이제 현금 대신 일반 신용카드로 가능합니다.</div>
            </div>

            <div className="mt-3.5">
              <button
                type="button"
                onClick={goStart}
                className="h-11 w-full rounded-2xl bg-white/15 px-3 text-[13px] font-extrabold shadow-sm ring-1 ring-white/25 backdrop-blur transition active:scale-[0.99] whitespace-nowrap"
              >
                지금 시작하기
              </button>            
            </div>

            
          </div>
        </div>

        {/* 배너 자동 전환 + 스와이프 */}
        {banners.length > 0 && (
          <div className="-mx-3">
            <BannerCarousel items={banners} onClick={onBannerClick} />
          </div>
        )}

        {/* 주류페이 소개 */}
        <div className="rounded-2xl bg-white px-5 py-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="text-[16px] font-extrabold text-slate-900">주류페이란?</div>
            <span className="rounded-full border border-slate-200 px-3 py-1 text-[10px] font-semibold text-slate-500">
              서비스 소개
            </span>
          </div>
          <div className="mt-4 space-y-2">
            {INTRO_POINTS.map((text, idx) => (
              <div key={idx} className="flex items-start gap-2 rounded-xl bg-slate-50 px-3 py-2">
                <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-600 text-white text-[10px] font-bold">
                  ✓
                </span>
                <span className="text-[12px] leading-relaxed text-slate-700 break-keep">{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* 사용자 후기 */}
        <div className="rounded-2xl bg-slate-50 px-5 py-6 shadow-sm">
          <div className="rounded-2xl bg-brand-600 px-4 py-5 text-white shadow-md">
            <div className="text-[13px] font-semibold leading-relaxed">
              “{TESTIMONIALS[testimonialIndex].quote}”
            </div>
          </div>
          <div className="mt-3 text-center">
            <div className="text-[12px] font-semibold text-slate-900">
              {TESTIMONIALS[testimonialIndex].name}
            </div>
            <div className="mt-1 text-[10px] text-slate-500">
              {TESTIMONIALS[testimonialIndex].tag}
            </div>
          </div>
          <div className="mt-4 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() =>
                setTestimonialIndex((v) => (v - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)
              }
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600"
              aria-label="이전 후기"
            >
              ←
            </button>
            <button
              type="button"
              onClick={() => setTestimonialIndex((v) => (v + 1) % TESTIMONIALS.length)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600"
              aria-label="다음 후기"
            >
              →
            </button>
          </div>
        </div>

        {/* 이용방법 */}
        <div className="rounded-2xl bg-white px-5 py-6 shadow-sm">
          <div className="text-center text-[18px] leading-snug font-extrabold text-slate-900 break-keep">
            정말 쉽고 간편한 결제 시스템
          </div>

          <div className="mt-5 grid grid-cols-2 gap-4">
            {FLOW_ITEMS.map((f) => (
              <div
                key={f.step}
                className="relative rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4 text-center"
              >
                <span className="absolute left-3 top-3 inline-flex items-center rounded-full bg-white px-2 py-0.5 text-[9px] font-extrabold text-slate-600 ring-1 ring-slate-200">
                  STEP {f.step}
                </span>

                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-b from-brand-500 to-brand-600 text-xl text-white shadow">
                  {f.icon}
                </div>

                <div className="mt-3 text-[13px] leading-snug font-extrabold text-slate-900 break-keep">
                  {f.title}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 무이자 할부 혜택 + 부분무이자 */}
        <div className="rounded-2xl bg-white px-5 py-6 shadow-sm">
          <div className="text-center text-[18px] leading-snug font-extrabold text-slate-900 break-keep">
            무이자 할부 혜택
          </div>
          <div className="mt-2 text-center text-[12px] text-slate-500 break-keep">
            카드사 별 최대 7개월까지 신용카드 무이자 할부
          </div>

          <div className="mt-5 space-y-3">
            {installmentDisplay.map((card) => (
              <div
                key={card.key}
                className="flex items-center justify-between rounded-2xl border border-slate-100 bg-white px-4 py-3"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-50 shrink-0">
                    {card.logo && (
                      <img
                        src={card.logo}
                        alt={card.name}
                        className="h-6 w-6 object-contain"
                        loading="lazy"
                      />
                    )}
                  </div>
                  <div className="text-[12px] font-semibold text-slate-900 break-keep truncate">
                    {card.name}
                  </div>
                </div>

                <div
                  className={`text-[12px] font-extrabold tracking-tight whitespace-nowrap ${
                    card.term === "할부 불가" ? "text-red-500" : "text-slate-900"
                  }`}
                >
                  {card.term}
                </div>
              </div>
            ))}

            {!installmentDisplay.length && (
              <div className="rounded-xl bg-slate-50 px-4 py-3 text-center text-[11px] text-slate-400">
                무이자 할부 정보가 아직 설정되지 않았습니다.
              </div>
            )}
          </div>

          {partialCards.length > 0 && (
            <div className="mt-6">
              <div className="flex flex-col items-center gap-2 text-center">
                <div className="text-[13px] font-extrabold text-slate-900 break-keep">
                  부분 무이자 할부 안내
                </div>
                <button
                  type="button"
                  onClick={() => setPartialOpen((v) => !v)}
                  aria-expanded={partialOpen}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-700"
                >
                  {partialOpen ? "안내 접기" : "안내 보기"}
                </button>
              </div>

              {partialOpen && (
                <div className="mt-3 space-y-3">
                  {partialCards.map((card) => (
                    <div
                      key={`partial-${card.key}`}
                      className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white shrink-0">
                          {card.logo && (
                            <img
                              src={card.logo}
                              alt={card.name}
                              className="h-6 w-6 object-contain"
                              loading="lazy"
                            />
                          )}
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-[12px] font-extrabold text-slate-900 break-keep truncate">
                              {card.name}
                            </div>
                            {card.partial_note ? (
                              <div className="text-[10px] text-slate-500 whitespace-nowrap">
                                {card.partial_note}
                              </div>
                            ) : null}
                          </div>

                          <div className="mt-2 whitespace-pre-line text-[11px] leading-relaxed text-slate-700 break-keep">
                            {card.partial_text}
                          </div>

                          {card.partial_note ? (
                            <div className="mt-2 text-[10px] text-slate-500 sm:hidden break-keep">
                              {card.partial_note}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        <div className="mt-4 rounded-xl bg-slate-50 px-4 py-3 text-[10px] text-slate-600 break-keep">
          <div className="font-extrabold text-slate-800">※ 안내</div>
          <ul className="mt-1 space-y-1">
            <li className="font-semibold text-slate-800">– 결제금액 5만원 이상부터 적용됩니다.</li>
            <li>– 일부 제외카드/은행계열카드는 무이자 적용이 제한될 수 있습니다.</li>
          </ul>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white px-5 py-6 shadow-sm">
        <div className="text-center text-[14px] font-extrabold text-slate-900 break-keep">
          주류페이에 대해 궁금한 점을 확인해보세요.
        </div>

        <div className="mt-4 space-y-3">
          {FAQ_ITEMS.map((item, idx) => (
            <div key={idx} className="space-y-2">
              <div className="flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-orange-500 text-white text-[10px] font-extrabold">
                  Q
                </span>
                <div className="text-[12px] font-semibold text-slate-900 break-keep">{item.q}</div>
              </div>
              <div className="flex items-start gap-2 rounded-xl bg-[#fff4cc] px-3 py-2">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-600 text-white text-[10px] font-extrabold">
                  W
                </span>
                <div className="text-[12px] text-slate-900 leading-relaxed break-keep">{item.a}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 flex justify-center">
          <button
            type="button"
            onClick={() => nav("/faq")}
            className="rounded-full border border-slate-200 px-5 py-2 text-[12px] font-semibold text-slate-700"
          >
            자주 묻는 질문
          </button>
        </div>
      </div>

      <div className="rounded-2xl bg-brand-50 px-5 py-6 text-slate-900 shadow-sm ring-1 ring-brand-100 break-keep">
        <div className="text-base font-extrabold">안전하게 이용하세요!</div>
        <p className="mt-2 text-[11px] leading-relaxed text-slate-700">
            전자금융거래법 제 28조 규정에 따라 주류페이 결제 시스템을 운영하고 있으며 결제와 정산을 철저히 관리하여
            고객님의 거래가 안전하게 이루어지도록 합니다.
          </p>
          <div className="mt-4 text-[11px] font-semibold text-slate-700">
            전자지급결제대행업 등록번호 02-004-00118
          </div>
        </div>
      </div>

      <KakaoChatButton />
      {/* QrAssetsButton 숨김 */}
      <HomeNoticeModal />
    </div>
  );
}
